import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Flex,
  List,
  Pagination,
  Popover,
  Table,
  TableColumnsType,
  Tag,
  Typography,
  theme,
} from "antd";
import { ApiOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import dayjs from "dayjs";
import { PaginationTotal, DownloadXlsButton } from "@/components";

const { Text } = Typography;

export const ACTIVE_STATUSES = ["pending", "creating"];
/** La fila que ocupa el lugar del Excel que se está subiendo, hasta que llega la real. */
const FILA_SUBIENDO = "__subiendo";

export type OrigenDeImportacion = "excel" | "integracion";

/** El back no guarda el origen: un Excel se reconoce por la extensión del nombre. */
export const origenDe = (record: any): OrigenDeImportacion =>
  /\.(xlsx?|xls)$/i.test(record?.name ?? "") ? "excel" : "integracion";

export const filasDe = (datos: any, origen: OrigenDeImportacion): any[] =>
  (datos?.data ?? []).filter((r: any) => origenDe(r) === origen);

interface HistorialDeImportacionesProps {
  origen: OrigenDeImportacion;
  /** Las importaciones del seller, de los dos orígenes: acá se quedan las de `origen`. */
  datos: any;
  tableProps: any;
  refetch?: () => any;
  /** El Excel que se está subiendo: se muestra en proceso hasta que llega su fila. */
  subiendo?: { name: string; createdAt: string } | null;
  /** Llegó la fila del Excel que se estaba subiendo. */
  onLlegoLaNueva?: () => void;
  /**
   * El link "Asociar" del aviso de coincidencias (xcons-docs#624): lleva a los productos de ese
   * import con 80% o más, todos seleccionados.
   */
  onAsociarCoincidencias?: (importId: number) => void;
}

/** El cálculo del % de match de un import sigue después de "Completado" (xcons-docs#624). */
const calculandoCoincidencias = (r: any) =>
  r?.match?.status === "queued" || r?.match?.status === "running";

/** Terminó de calcular y encontró no asociados con 80% o más: hay algo para asociar. */
const hayCoincidencias = (r: any) =>
  !!r?.match?.notify && r.match.status === "done" && (r.match.found ?? 0) > 0;

/**
 * El historial de un panel de carga (xcons-docs#609): el de Excel muestra sólo los Excel y el de
 * Integrar sólo las sincronizaciones del ERP. Mientras una está en proceso se refresca solo, y
 * cuando termina su "Completado" entra con un pulso.
 */
export const HistorialDeImportaciones = React.forwardRef<
  HTMLDivElement,
  HistorialDeImportacionesProps
>(({ origen, datos, tableProps, refetch, subiendo, onLlegoLaNueva, onAsociarCoincidencias }, ref) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const filas = useMemo(() => filasDe(datos, origen), [datos, origen]);

  // También mientras se calculan las coincidencias: el aviso llega con el refresco.
  const hayActivas = filas.some(
    (r) => ACTIVE_STATUSES.includes(r.status) || calculandoCoincidencias(r),
  );
  useEffect(() => {
    if (!hayActivas || !refetch) return;
    const interval = setInterval(() => refetch(), 3000);
    return () => clearInterval(interval);
  }, [hayActivas, refetch]);

  useEffect(() => {
    if (subiendo) setCurrentPage(1);
  }, [subiendo]);

  /**
   * Las que terminaron en este refresco: su "Completado" entra con un pulso. Son las que estaban en
   * proceso en el refresco anterior, o la que llegó nueva y ya terminada mientras se subía un
   * Excel. Una importación que ya estaba terminada al abrir el panel no se anima.
   */
  const esperandoNueva = useRef(false);
  esperandoNueva.current = !!subiendo;
  const activasAntes = useRef<Set<string>>(new Set());
  const idsAntes = useRef<Set<string> | null>(null);
  const [recienTerminadas, setRecienTerminadas] = useState<Set<string>>(
    new Set(),
  );
  useEffect(() => {
    if (!datos) return;
    const activas = new Set(
      filas
        .filter((r) => ACTIVE_STATUSES.includes(r.status))
        .map((r) => r.importId),
    );
    const nuevas = idsAntes.current
      ? filas.filter((r) => !idsAntes.current!.has(r.importId))
      : [];
    const terminaron = filas
      .filter(
        (r) =>
          !activas.has(r.importId) &&
          (activasAntes.current.has(r.importId) ||
            (esperandoNueva.current && nuevas.includes(r))),
      )
      .map((r) => r.importId);
    if (esperandoNueva.current && nuevas.length) onLlegoLaNueva?.();
    activasAntes.current = activas;
    idsAntes.current = new Set(filas.map((r) => r.importId));
    if (!terminaron.length) return;
    setRecienTerminadas((prev) => new Set([...prev, ...terminaron]));
    const timeout = setTimeout(
      () =>
        setRecienTerminadas((prev) => {
          const siguen = new Set(prev);
          terminaron.forEach((id) => siguen.delete(id));
          return siguen;
        }),
      1500,
    );
    return () => clearTimeout(timeout);
  }, [filas]);

  /**
   * La última importación con coincidencias para asociar: su acceso va de lado a lado dentro de
   * su fila, en verde suave, con el botón del tamaño de Cargar XLS (xcons-docs#630). En la celda
   * del Informe quedaba escondido. Es la última **con** coincidencias y no la última a secas: un
   * archivo que falló después no tiene que esconder lo que quedó por asociar. Las anteriores
   * siguen con el link en la celda.
   */
  const ultima = filas
    .filter(hayCoincidencias)
    .reduce<any>(
      (u, r) => (!u || dayjs(r.createdAt).isAfter(u.createdAt) ? r : u),
      null,
    );
  const ultimaConCoincidencias =
    !subiendo && ultima ? ultima.importId : undefined;

  const columns: TableColumnsType<any> = [
    {
      key: "date",
      title: t("products.import.table.date"),
      dataIndex: "createdAt",
      // Al ancho de la fecha: el ancho mínimo y el `nowrap` la dejan justa.
      width: 1,
      render: (date) => (
        <Flex vertical style={{ whiteSpace: "nowrap" }}>
          <Text>{dayjs(date).format("DD/MM/YY")}</Text>
          <Text type="secondary">{dayjs(date).format("HH:mm")}</Text>
        </Flex>
      ),
    },
    {
      key: "file",
      title: t("products.import.table.fileName"),
      dataIndex: "file_name",
      render: (_, record) => {
        // Sin tags: en proceso lo dice "Integrando…" en el Informe (xcons-docs#609).
        return (
          <Flex gap={14} style={{ minWidth: 200 }}>
            <Flex gap={8} vertical>
              <Text style={{ color: token.colorTextSecondary }}>
                {record.name}
              </Text>
            </Flex>
            {/* También en las integraciones: sin archivo, arma un Excel con lo que mandó el ERP
                (precio, stock…), para poder contestar de dónde salió un dato. Si el backend no
                guardó los productos, avisa que no hay datos (xcons-docs#629). */}
            {record.importId !== FILA_SUBIENDO && (
              <DownloadXlsButton
                importId={record.importId}
                fileName={record.name}
                errorDetails={record.errorDetails}
                fileKey={record.fileKey}
              />
            )}
          </Flex>
        );
      },
    },
    {
      key: "status",
      title: t("products.import.table.report"),
      dataIndex: "status",
      align: "end",
      render: (status, record) => {
        if (status === "creating" || status === "pending") {
          // Sólo "Integrando…" con un spinner (xcons-docs#609): los pasos no entraban en el
          // panel y no decían nada que el operador pueda usar.
          return (
            <Flex gap={6} justify="end" align="center">
              <LoadingOutlined spin style={{ color: token.colorPrimary }} />
              <Text>Integrando…</Text>
            </Flex>
          );
        }

        if (status === "completed") {
          const total =
            (record.updated ?? 0) +
            (record.enabled ?? 0) +
            (record.notUpdated ?? 0);
          return (
            <Flex gap={14} justify="end" align="center">
              <Flex vertical style={{ textAlign: "right" }}>
                <Flex
                  gap={4}
                  justify="end"
                  align="center"
                  // Al terminar, el "Completado" entra con un pulso.
                  className={
                    recienTerminadas.has(record.importId)
                      ? "tag-importacion--termino"
                      : undefined
                  }
                >
                  <Badge status="success" />
                  <Text style={{ textTransform: "capitalize" }}>
                    {t("products.import.table.report.completed")}
                  </Text>
                </Flex>
                <Text
                  style={{
                    color: token.colorTextSecondary,
                    fontSize: token.fontSizeSM,
                  }}
                >
                  {t("products.show.tableUpdated")} {record.updated ?? 0} ·{" "}
                  {t("products.import.table.report.created")}{" "}
                  {record.enabled ?? 0}
                  {total > 0 &&
                    ` · ${t("products.import.table.report.total")} ${total}`}
                </Text>
                {/* Las coincidencias con el catálogo, sólo en lo que subió una persona
                    (xcons-docs#624): mientras se calculan, el avance; al terminar, cuántos
                    no asociados quedaron con 80% o más y el link para asociarlos. */}
                {record.match?.notify && calculandoCoincidencias(record) && (
                  <Flex gap={6} justify="end" align="center" style={{ marginTop: 4 }}>
                    <LoadingOutlined style={{ color: token.colorPrimary }} />
                    <Text style={{ fontSize: token.fontSizeSM }}>
                      Analizando coincidencias {record.match.done} / {record.match.total}
                    </Text>
                  </Flex>
                )}
                {hayCoincidencias(record) &&
                  record.importId !== ultimaConCoincidencias && (
                    <Flex gap={6} justify="end" align="center" style={{ marginTop: 4 }}>
                      <Text style={{ fontSize: token.fontSizeSM }}>
                        Encontramos {record.match.found} producto
                        {record.match.found !== 1 ? "s" : ""} no asociado
                        {record.match.found !== 1 ? "s" : ""} con 80% de match
                      </Text>
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => onAsociarCoincidencias?.(Number(record.importId))}
                      >
                        {t("buttons.link")}
                      </Button>
                    </Flex>
                  )}
                {(record.validationErrors ?? 0) > 0 && (
                  <Flex
                    gap={6}
                    justify="end"
                    align="center"
                    style={{ marginTop: 4 }}
                  >
                    <Popover
                      placement="topRight"
                      title={`${record.validationErrors} fila${
                        record.validationErrors !== 1 ? "s" : ""
                      } con errores`}
                      content={
                        <List
                          size="small"
                          style={{
                            maxHeight: 300,
                            overflowY: "auto",
                            minWidth: 280,
                          }}
                          dataSource={record.errorDetails ?? []}
                          renderItem={(row: any) => (
                            <List.Item
                              style={{
                                flexDirection: "column",
                                alignItems: "flex-start",
                                padding: "4px 0",
                              }}
                            >
                              <Text
                                strong
                                style={{ fontSize: token.fontSizeSM }}
                              >
                                SKU: {row.rawSku || "(sin SKU)"}
                              </Text>
                              {row.errors?.map((e: any, i: number) => (
                                <Text
                                  key={i}
                                  style={{
                                    color: token.colorError,
                                    fontSize: token.fontSizeSM,
                                  }}
                                >
                                  • {e.field}: {e.message}
                                </Text>
                              ))}
                            </List.Item>
                          )}
                        />
                      }
                    >
                      <Tag color="warning" style={{ cursor: "pointer" }}>
                        {record.validationErrors} fila
                        {record.validationErrors !== 1 ? "s" : ""} ignoradas —
                        ver detalle
                      </Tag>
                    </Popover>
                    <DownloadXlsButton
                      importId={record.importId}
                      fileName={record.name}
                      errorDetails={record.errorDetails}
                      reportMode
                    />
                  </Flex>
                )}
              </Flex>
            </Flex>
          );
        }

        if (status === "error") {
          // El motivo a la vista, no detrás de un ⓘ (xcons-docs#609). El texto genérico
          // habla de un archivo: sólo si el backend no mandó motivo.
          // Con el botón del informe al lado, como las filas ignoradas (xcons-docs#630).
          return (
            <Flex gap={14} justify="end" align="center">
              <Flex vertical style={{ textAlign: "right" }}>
                <Flex gap={4} justify="end" align="center">
                  <Badge status="error" />
                  <Text>Error</Text>
                </Flex>
                <Text
                  style={{
                    color: token.colorTextSecondary,
                    fontSize: token.fontSizeSM,
                  }}
                >
                  {record.additionalData?.error_msg ||
                    t("products.import.table.report.tooltipError")}
                </Text>
              </Flex>
              <DownloadXlsButton
                importId={record.importId}
                fileName={record.name}
                errorDetails={record.errorDetails}
                reportMode
              />
            </Flex>
          );
        }

        // Fallback for unknown statuses
        return <Badge status="default" text={status} />;
      },
    },
  ];

  const paginadas = filas.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <>
      <div ref={ref} style={{ scrollMarginTop: 24 }} />
      {(filas.length > 0 || subiendo) && (
        <Table
          {...tableProps}
          className="table-bordered"
          pagination={false}
          style={{ marginTop: token.marginLG }}
          scroll={{ x: true }}
          rowKey="importId"
          dataSource={
            subiendo
              ? [
                  {
                    ...subiendo,
                    importId: FILA_SUBIENDO,
                    status: "creating",
                  },
                  ...paginadas,
                ]
              : paginadas
          }
          columns={columns}
          rowClassName={(r) =>
            r.importId === ultimaConCoincidencias
              ? "importacion--con-coincidencias"
              : ""
          }
          expandable={{
            showExpandColumn: false,
            expandedRowClassName: () => "importacion__coincidencias",
            expandedRowKeys: ultimaConCoincidencias
              ? [ultimaConCoincidencias]
              : [],
            rowExpandable: (r) => r.importId === ultimaConCoincidencias,
            expandedRowRender: (r) => (
              <Flex
                justify="space-between"
                align="center"
                gap={16}
                wrap="wrap"
                style={{
                  padding: "12px 16px",
                  borderRadius: token.borderRadiusLG,
                  background: token.colorPrimaryBg,
                }}
              >
                <Text>
                  Encontramos {r.match.found} producto
                  {r.match.found !== 1 ? "s" : ""} no asociado
                  {r.match.found !== 1 ? "s" : ""} con 80% de match
                </Text>
                <Button
                  type="primary"
                  size="middle"
                  icon={<ApiOutlined />}
                  iconPosition="end"
                  onClick={() => onAsociarCoincidencias?.(Number(r.importId))}
                >
                  {t("buttons.link")}
                </Button>
              </Flex>
            ),
          }}
          footer={() => (
            <div>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filas.length}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
                showTotal={(total) => (
                  <PaginationTotal total={total} entityName="documents" />
                )}
              />
            </div>
          )}
        />
      )}
    </>
  );
});
